import React, { useState, useRef, useEffect, useCallback, LegacyRef, createRef } from "react";
import ReactPlayer from "react-player";
import "../../styles.css";
import ExerciseGridView from "./Grid/ExerciseGridView";
import ExerciseSpotlightView from "./Spotlight/ExerciseSpotlightView";
import AudioPlayer from 'react-h5-audio-player';
import H5AudioPlayer from "react-h5-audio-player";
import 'react-h5-audio-player/lib/styles.css';
import ExerciseControl from './ExerciseControl';
import { Label, VideoContainer } from "./ExerciseVideo.css";
import { AnyStyledComponent } from "styled-components";

interface VideoPlayerProps {
  originalLinks: {
    url1: string;
    url2: string;
    url3: string;
    url4: string;
    audioSource: NodeRequire;
    title: string;
    level: string;
    apparatus: string;
    props: string;
    body: string;
  };
}

function almost(a: number, b: number, delta = 0.01) {
  return Math.abs(a - b) < delta
}

function ExerciseVideo({ originalLinks }: VideoPlayerProps) {
  const [grid, setGrid] = useState(true);
  const [spotlight, setSpotlight] = useState(1);
  const audio = useRef<AudioPlayer>(null);

  const videoPlayerRef1 = useRef<ReactPlayer>(null);
  const videoPlayerRef2 = useRef<ReactPlayer>(null);
  const videoPlayerRef3 = useRef<ReactPlayer>(null);
  const videoPlayerRef4 = useRef<ReactPlayer>(null);

  const [videoState, setVideoState] = useState({
    playing: false,
    muted: true,
    volume: 0.5,
    seeking: false,
    Buffer: true,
    loop: true
  });

  const { 
    audioSource,
    title,
    level,
    apparatus,
    props,
    body
  } = originalLinks;

  // Destructuring the properties from the videoState
  const {
    playing,
    seeking,
    loop
  } = videoState;

  const [currentTime, setCurrentTime] = useState<number>(0);
  const [muted, setMuted] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(1);
  const currentTimeAfterRerender = React.useRef<number>(0);
  const updateCurrentTimeAfterRerender = React.useRef<boolean>(false);

  useEffect(() => {
    if (!grid && updateCurrentTimeAfterRerender.current) {
      setCurrentTime(currentTimeAfterRerender.current);
      updateCurrentTimeAfterRerender.current = false;
      synchronizeVideosToCurrentTime(currentTimeAfterRerender.current);
    }
  });

  const [links, setLinks] = useState({
    url1: originalLinks.url1,
    url2: originalLinks.url2,
    url3: originalLinks.url3,
    url4: originalLinks.url4,
  });

  const playPauseHandler = () => {
    if (videoState.playing && audio.current) {
      audio.current?.audio.current?.pause();
    }
    else if (audio.current) {
      audio.current?.audio.current?.play();
    }
    setVideoState({ ...videoState, playing: !videoState.playing });
  };

  const handleMute = () => {
    if (audio.current && audio.current.isPlaying()) {
      audio.current?.audio.current?.pause();
      setMuted(true);
    }
    else if (audio.current) {
      audio.current?.audio.current?.play();
      setMuted(false);
    }
  };

  const fastForwardOnRef = (videoRef: React.RefObject<ReactPlayer>) => {
    videoRef.current?.seekTo(videoRef.current?.getCurrentTime() + 10);
  };

  const rewindOnRef = (videoRef: React.RefObject<ReactPlayer>) => {
    videoRef.current?.seekTo(videoRef.current?.getCurrentTime() - 10);
  };

  const restartOnRef = (videoRef: React.RefObject<ReactPlayer>) => {
    videoRef.current?.seekTo(0);
  };

  const handleSeekChange = (e: any) => {
    videoPlayerRef1.current?.seekTo(e.target.value);
    videoPlayerRef2.current?.seekTo(e.target.value);
    videoPlayerRef3.current?.seekTo(e.target.value);
    videoPlayerRef4.current?.seekTo(e.target.value);
  }

  const updateRefsWithCurrentTime = () => {
    const videoTime = videoPlayerRef1.current?.getCurrentTime();
    if (videoTime && !almost(videoTime, currentTime)) {
      videoPlayerRef1.current?.seekTo(currentTime);
      videoPlayerRef2.current?.seekTo(currentTime);
      videoPlayerRef3.current?.seekTo(currentTime);
      videoPlayerRef4.current?.seekTo(currentTime);
    }
  }

  const synchronizeVideosToCurrentTime = (newCurrentTime: number) => {
    videoPlayerRef1.current?.seekTo(newCurrentTime);
    videoPlayerRef2.current?.seekTo(newCurrentTime);
    videoPlayerRef3.current?.seekTo(newCurrentTime);
    videoPlayerRef4.current?.seekTo(newCurrentTime);
    //console.log(`all videos synchronized to ${newCurrentTime}`);
    setCurrentTime(newCurrentTime);
  };

  const onAudioEnded = () => {
    setVideoState({ ...videoState, playing: false });
  };

  const handleProgress = (state: AnyStyledComponent) => {
    if (!seeking) {
      setCurrentTime(state.playedSeconds);
    }
  };

  const handleVolumeChange = (e: any) => {
    if (e.target.value === 0) {
      setMuted(true);
    } else {
      setVolume(e.target.value);
    }
  }

  const fastForward = () => {
    if (
      videoPlayerRef1 !== null &&
      videoPlayerRef2 !== null &&
      videoPlayerRef3 !== null &&
      videoPlayerRef4 !== null
    ) {
      fastForwardOnRef(videoPlayerRef1);
      fastForwardOnRef(videoPlayerRef2);
      fastForwardOnRef(videoPlayerRef3);
      fastForwardOnRef(videoPlayerRef4);
    }
  };

  const rewind = () => {
    if (
      videoPlayerRef1 !== null &&
      videoPlayerRef2 !== null &&
      videoPlayerRef3 !== null &&
      videoPlayerRef4 !== null
    ) {
      rewindOnRef(videoPlayerRef1);
      rewindOnRef(videoPlayerRef2);
      rewindOnRef(videoPlayerRef3);
      rewindOnRef(videoPlayerRef4);
    }
  };

  const restart = () => {
    if (
      videoPlayerRef1 !== null &&
      videoPlayerRef2 !== null &&
      videoPlayerRef3 !== null &&
      videoPlayerRef4 !== null
    ) {
      restartOnRef(videoPlayerRef1);
      restartOnRef(videoPlayerRef2);
      restartOnRef(videoPlayerRef3);
      restartOnRef(videoPlayerRef4);
      audio.current?.audio.current?.load();
      audio.current?.audio.current?.play();
    }
  };

  const switchPlayerToGrid = (
    videoRef: React.RefObject<ReactPlayer>
  ) => {
    if (!grid && videoRef.current) {
      setCurrentTime(videoRef.current.getCurrentTime());
      setGrid(true);
    }
  };

  const switchPlayerToSpotlight = (
    videoNumber: number,
    videoRef: React.RefObject<ReactPlayer>,
    newCurrentTime: number
  ) => {
    if (grid && videoRef.current) {
      setSpotlight(videoNumber);
      setGrid(false);
      currentTimeAfterRerender.current = newCurrentTime;
      updateCurrentTimeAfterRerender.current = true;
    }
  };

  const updateSpotlight = (
    videoNumber: number,
    newCurrentTime: number
  ) => {
    setSpotlight(videoNumber);
    currentTimeAfterRerender.current = newCurrentTime;
    updateCurrentTimeAfterRerender.current = true;
  }

  return (
    <div>
    <VideoContainer grid={grid}>
      <AudioPlayer
        style={{display: 'none'}}
        autoPlay={false}
        src={audioSource?.toString()}
        ref={audio}
        onEnded={onAudioEnded}
        hasDefaultKeyBindings={false}
      />
        {grid ? (
          <ExerciseGridView
            switchPlayerToSpotlight={switchPlayerToSpotlight}
            currentTime={currentTime}
            playPauseHandler={playPauseHandler}
            playing={playing}
            loop={loop}
            ref1={videoPlayerRef1}
            ref2={videoPlayerRef2}
            ref3={videoPlayerRef3}
            ref4={videoPlayerRef4}
            links={links}
            handleProgress={handleProgress}
            updateRefsWithCurrentTime={updateRefsWithCurrentTime}
          />
          ) : (
          <ExerciseSpotlightView
            switchPlayerToGrid={switchPlayerToGrid}
            playPauseHandler={playPauseHandler}
            playing={playing}
            loop={loop}
            ref1={videoPlayerRef1}
            ref2={videoPlayerRef2}
            ref3={videoPlayerRef3}
            ref4={videoPlayerRef4}
            links={links}
            spotlight={spotlight}
            setSpotlight={setSpotlight}
            handleProgress={handleProgress}
            updateRefsWithCurrentTime={updateRefsWithCurrentTime}
            updateSpotlight={updateSpotlight}
          />
        )}
      <ExerciseControl
          onPlayPause={playPauseHandler}
          playing={playing}
          restart={restart}
          fastForward={fastForward}
          rewind={rewind}
          switchPlayerToGrid={switchPlayerToGrid}
          muteControl={handleMute}
          muted={muted}
          currentTime={currentTime}
          handleSeekChange={handleSeekChange}
          handleVolumeChange={handleVolumeChange}
          volume={volume}
          videoRef={videoPlayerRef1}
          grid={grid}
        />
    </VideoContainer>
    <Label>
      {title}
      <p>Level: {level} | Apparatus: {apparatus} | Props: {props} | Body: {body}</p>
    </Label>
  </div>
  )
}

export default ExerciseVideo;
